import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  TrashIcon,
  ArrowUturnLeftIcon,
  ShieldCheckIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { WHATSAPP_GREEN } from './qualifyingAgentStatus';
import { isKnockout, isMandatory } from './QualifyingAgentQuestionsEditor';

/**
 * A screening's Qualifying Agent configuration, opened from the "Screening details" modal. Mirrors
 * how the Assessment and AI Interview cards there open their own detail views.
 *
 * Since 22 Oct the recruiter can also, without opening the full editor, mark which of their own
 * questions are mandatory and delete questions (product request). The intro is never deletable,
 * and the role brief is switched on or off in the editor, not deleted here. Saved through the same
 * PUT as the editor (`qualifyingAgentQuestions`), so the server cleans it the same way. Each session
 * freezes its questions when it starts, so a change applies to candidates invited from now on;
 * nobody's screening changes mid-way.
 *
 * Props: screening, onClose, onSaved (called after a successful save, to refetch).
 */
const KIND_LABEL = {
  intro: 'Intro',
  willingness: 'Role brief',
  salary_expectation: 'Salary',
  notice_period: 'Notice period',
  ai_interview_consent: 'Interview consent',
};

const MODE_LABEL = {
  call: 'Phone call only',
  whatsapp: 'WhatsApp only',
  both: 'Phone call and WhatsApp',
};

// The intro and the role brief open every call; they are not the recruiter's own questions.
const isOwn = (q) => q.kind !== 'intro' && q.kind !== 'willingness';

export default function QualifyingAgentDetailsModal({ screening, onClose, onSaved }) {
  const saved = useMemo(() => screening?.qualifyingAgentQuestions || [], [screening]);
  const mode = screening?.qualifyingAgentMode || 'both';
  // Unset = on: screenings saved before the switch keep their brief (server does the same).
  const briefOn = screening?.qualifyingAgentRoleBrief !== false;

  // Working copy: `removed` marks a question for deletion until Save, so it can be undone.
  const [rows, setRows] = useState(() => saved.map((q) => ({ q, removed: false })));
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setRows(saved.map((q) => ({ q, removed: false })));
  }, [saved]);

  const dirty = rows.some(
    ({ q, removed }, i) => removed || Boolean(q.mandatory) !== Boolean(saved[i]?.mandatory)
  );
  const kept = rows.filter((r) => !r.removed).map((r) => r.q);
  // Same rule as the editor: with the brief off, the recruiter's questions are all the call asks.
  const problem =
    kept.length === 0
      ? 'Keep at least one question.'
      : !briefOn && !kept.some(isOwn)
        ? 'Keep at least one of your own questions, or turn the role brief on in Edit.'
        : '';

  const setMandatory = (index, mandatory) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, q: { ...r.q, mandatory } } : r)));
  const setRemoved = (index, removed) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, removed } : r)));

  const save = async () => {
    if (problem || !dirty) {
      return;
    }
    setSaving(true);
    try {
      await axios.put(`/api/screenings/${screening._id}`, { qualifyingAgentQuestions: kept });
      toast.success('Questions updated — they apply to candidates invited from now on');
      await onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save the questions');
    } finally {
      setSaving(false);
    }
  };

  // Numbered as the call asks them: a role brief that is off is not asked.
  let number = 0;

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/50 p-4 sm:p-6">
      <div className="flex w-full max-w-2xl max-h-[85vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div style={{ height: 3, background: 'linear-gradient(90deg, #007BFF, #0062CC)' }} />
        <header className="flex items-center justify-between shrink-0 border-b border-gray-100 px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#007BFF]">
              <PhoneIcon className="text-white" style={{ width: 18, height: 18 }} />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight text-slate-900">Qualifying Agent</h2>
              <p className="text-xs mt-0.5 text-slate-500">
                What each candidate is asked, and on which channel
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
          <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Channels</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {(mode === 'call' || mode === 'both') && (
                <span className="inline-flex items-center gap-1.5 rounded-lg border border-[#007BFF]/20 bg-[#007BFF]/5 px-2.5 py-1 text-xs font-semibold text-[#0062CC]">
                  <PhoneIcon className="h-3.5 w-3.5" />
                  Phone call
                </span>
              )}
              {(mode === 'whatsapp' || mode === 'both') && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold"
                  style={{ backgroundColor: '#F0FDF4', borderColor: '#BBF7D0', color: '#15803D' }}
                >
                  <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" style={{ color: WHATSAPP_GREEN }} />
                  WhatsApp
                </span>
              )}
            </div>
            <p className="mt-2.5 text-xs text-gray-600">
              {MODE_LABEL[mode]}
              {mode === 'both' &&
                ' — if the candidate doesn’t finish on one channel, the agent continues on the other.'}
            </p>
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                Questions
              </p>
              <span className="text-xs font-semibold text-gray-500">{kept.length}</span>
            </div>

            {saved.length === 0 ? (
              // A screening with no configured questions still runs — the agent falls back to a
              // built-in default set — so this says exactly that rather than implying it is broken.
              <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm font-semibold text-amber-800">No custom questions set</p>
                <p className="mt-1 text-xs text-amber-700 leading-relaxed">
                  Candidates are asked the built-in default set (willingness, salary, notice
                  period and interview consent). Edit the screening to ask your own questions.
                </p>
              </div>
            ) : (
              <>
                <p className="mt-1 text-[11px] text-gray-500">
                  Mark your own questions as mandatory, or delete questions. The intro is always
                  mandatory: once it and the questions you mark are answered, the agent stops
                  calling. Until then it calls again, up to the call limit.
                </p>
                <div className="mt-2 space-y-2">
                  {rows.map(({ q, removed }, index) => {
                    const briefOff = q.kind === 'willingness' && !briefOn;
                    const asked = !removed && !briefOff;
                    if (asked) {
                      number += 1;
                    }
                    const own = isOwn(q);
                    return (
                      <div
                        key={q.id || index}
                        data-testid="qa-details-question"
                        className={`rounded-xl border p-3.5 ${
                          removed ? 'border-dashed border-gray-200 bg-gray-50' : 'border-gray-100 bg-white'
                        }`}
                      >
                        <div className="flex items-start gap-2.5">
                          <span
                            className={`text-[13px] font-extrabold shrink-0 ${asked ? 'text-[#007BFF]' : 'text-gray-300'}`}
                          >
                            {asked ? `Q${number}` : '—'}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p
                              className={`text-[14px] font-medium leading-snug ${
                                removed ? 'text-gray-400 line-through' : briefOff ? 'text-gray-400' : 'text-gray-900'
                              }`}
                            >
                              {q.text}
                            </p>
                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                              {q.kind && (
                                <span className="inline-block rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold text-gray-500">
                                  {KIND_LABEL[q.kind] || (q.kind === 'custom' ? 'Your question' : q.kind)}
                                </span>
                              )}
                              {briefOff && (
                                <span className="text-[11px] text-gray-400">Off — not played on the call</span>
                              )}
                              {isKnockout(q) && !briefOff && (
                                <span className="inline-flex items-center gap-1 rounded-md bg-red-50 px-1.5 py-0.5 text-[11px] font-semibold text-red-600">
                                  <BoltIcon className="h-3 w-3" />
                                  Knockout
                                </span>
                              )}
                              {removed && (
                                <span className="text-[11px] font-medium text-red-500">Will be deleted when you save</span>
                              )}
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-1.5">
                            {q.kind === 'intro' && isMandatory(q) && (
                              <span
                                className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-2 py-1 text-xs font-semibold text-teal-700"
                                title="The intro is always mandatory"
                                data-testid="qa-details-intro-mandatory"
                              >
                                <ShieldCheckIcon className="h-3.5 w-3.5" />
                                Mandatory
                              </span>
                            )}
                            {own && !removed && (
                              <label
                                className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-semibold transition-colors ${
                                  q.mandatory
                                    ? 'border-teal-200 bg-teal-50 text-teal-700'
                                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={Boolean(q.mandatory)}
                                  onChange={(e) => setMandatory(index, e.target.checked)}
                                  data-testid="qa-details-mandatory"
                                  className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600 focus:ring-teal-200"
                                />
                                <ShieldCheckIcon className="h-3.5 w-3.5" />
                                Mandatory
                              </label>
                            )}
                            {q.kind !== 'intro' &&
                              (removed ? (
                                <button
                                  type="button"
                                  onClick={() => setRemoved(index, false)}
                                  data-testid="qa-details-undo"
                                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
                                >
                                  <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                                  Undo
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setRemoved(index, true)}
                                  aria-label={`Delete question: ${q.text}`}
                                  title="Delete question"
                                  data-testid="qa-details-delete"
                                  className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {dirty && (
          <footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50/60 px-6 py-3">
            <p className={`text-xs ${problem ? 'font-medium text-red-600' : 'text-gray-500'}`}>
              {problem || 'Applies to candidates invited from now on; screenings already started keep their questions.'}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setRows(saved.map((q) => ({ q, removed: false })))}
                disabled={saving}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving || Boolean(problem)}
                data-testid="qa-details-save"
                className="rounded-lg bg-[#007BFF] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#0062CC] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}
